import React from 'react';
import Header from '../components/Header';
import Hero from '../components/Hero';
import About from '../components/About';
import Guides from '../components/Guides';
import FAQ from '../components/FAQ';
import News from '../components/News';
import Contact from '../components/Contact';
import Footer from '../components/Footer';
import AgeVerification from '../components/AgeVerification';
import CookieConsent from '../components/CookieConsent';
import { Star, ExternalLink, Zap, Crown } from 'lucide-react';

const Slots = () => {
  const slotsOffers = [
    {
      id: 1,
      name: 'Mr. Play',
      url: 'https://www.mrplay.com/',
      bonus: '100 spins + 100% up to £200 bonus',
      description: 'A sophisticated gaming destination that combines elegance with excitement, featuring premium slots with outstanding graphics, innovative features, and substantial welcome packages.',
      features: ['Premium Slots Collection', 'Free Spins Included', 'High RTP Games', 'Sophisticated Experience'],
      color: 'from-book-brown to-book-brown-light',
      icon: <Crown className="h-8 w-8" />,
      layout: 'horizontal',
      slotsRating: 'Excellent'
    },
    {
      id: 2,
      name: 'NRG',
      url: 'https://nrg.bet/',
      bonus: 'Get 200% on your first deposit + 100 free spins',
      description: 'An energetic gaming platform that delivers explosive slots gaming with high-energy entertainment, generous bonuses, and modern slot titles that keep players engaged and excited.',
      features: ['High-Energy Slots', 'Generous Free Spins', 'Modern Game Library', 'Vibrant Experience'],
      color: 'from-book-brown-dark to-book-brown',
      icon: <Star className="h-8 w-8" />,
      layout: 'vertical',
      slotsRating: 'Very Good'
    },
    {
      id: 3,
      name: 'PUB',
      url: 'https://www.pubcasino.co.uk/',
      bonus: '100% up to £100',
      description: 'A beloved UK casino brand that brings classic slots gaming with authentic pub atmosphere, featuring traditional fruit machines and modern video slots in a familiar, community-focused environment.',
      features: ['Classic UK Slots', 'Authentic Gaming', 'Community Atmosphere', 'Fair Play Guarantee'],
      color: 'from-book-sepia to-book-brown-light',
      icon: <Zap className="h-8 w-8" />,
      layout: 'card',
      slotsRating: 'Good'
    }
  ];

  return (
    <div className="min-h-screen bg-book-cream">
      <Header />

      {/* Slots-Focused Hero */}
      <section className="relative bg-gradient-to-br from-book-brown via-book-brown-light to-book-sepia text-book-cream overflow-hidden">
        <div className="absolute inset-0 opacity-10">
          <div className="absolute inset-0" style={{
            backgroundImage: 'radial-gradient(circle, rgba(255,255,255,0.1) 1px, transparent 1px)',
            backgroundSize: '20px 20px'
          }}></div>
        </div>

        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-20 lg:py-28">
          <div className="text-center">
            <div className="flex justify-center items-center space-x-2 mb-6">
              <Crown className="h-8 w-8 text-book-gold" />
              <h1 className="text-book-cream text-4xl md:text-6xl lg:text-7xl font-bold tracking-tight">
                Premium Slots Experience
              </h1>
              <Star className="h-8 w-8 text-book-gold" />
            </div>

            <p className="text-xl md:text-2xl lg:text-3xl mb-8 max-w-4xl mx-auto leading-relaxed">
              Discover the ultimate slots gaming destination in the UK, where premium titles meet exceptional
              bonuses and world-class entertainment. Experience the thrill of spinning with confidence.
            </p>

            <div className="flex flex-col sm:flex-row justify-center items-center space-y-4 sm:space-y-0 sm:space-x-6 mb-12">
              <div className="flex items-center space-x-2">
                <Crown className="h-5 w-5 text-book-gold" />
                <span className="text-lg font-medium">Premium Slots</span>
              </div>
              <div className="flex items-center space-x-2">
                <Zap className="h-5 w-5 text-book-gold" />
                <span className="text-lg font-medium">Generous Bonuses</span>
              </div>
              <div className="flex items-center space-x-2">
                <Star className="h-5 w-5 text-book-gold" />
                <span className="text-lg font-medium">Trusted Platforms</span>
              </div>
            </div>

            <div className="flex flex-col sm:flex-row justify-center space-y-4 sm:space-y-0 sm:space-x-4">
              <a href="#slots-offers" className="bg-book-gold text-book-brown hover:bg-book-beige px-8 py-4 rounded-lg font-semibold text-lg transition-colors duration-200 shadow-lg">
                Explore Premium Slots
              </a>
              <a href="#about" className="bg-transparent border-2 border-book-cream text-book-cream hover:bg-book-cream hover:text-book-brown px-8 py-4 rounded-lg font-semibold text-lg transition-colors duration-200">
                Learn More
              </a>
            </div>
          </div>

          <div className="absolute top-10 left-10 opacity-20">
            <div className="w-20 h-20 border-2 border-book-gold rounded-full"></div>
          </div>
          <div className="absolute bottom-10 right-10 opacity-20">
            <div className="w-16 h-16 border-2 border-book-gold transform rotate-45"></div>
          </div>
        </div>
      </section>

      {/* Slots Offers Section */}
      <section id="slots-offers" className="py-16 px-4 md:px-8 lg:px-16 bg-gradient-to-b from-book-beige to-book-cream">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-16">
            <h2 className="text-4xl md:text-5xl font-bold text-book-brown mb-6">
              Top Slots Platforms
            </h2>
            <p className="text-xl text-book-brown-dark max-w-3xl mx-auto">
              Carefully selected platforms offering the finest slots gaming experiences in the UK market,
              ranked by slots gaming excellence and player satisfaction.
            </p>
          </div>

          <div className="space-y-12">
            {/* Mr. Play - Horizontal Layout */}
            <div className="bg-gradient-to-r from-book-beige to-book-cream rounded-xl shadow-book overflow-hidden">
              <div className="flex flex-col lg:flex-row">
                <div className="lg:w-1/3 bg-gradient-to-br from-book-brown to-book-brown-light p-8 flex items-center justify-center">
                  <div className="text-center text-book-cream">
                    <Crown className="h-16 w-16 mx-auto mb-4" />
                    <h3 className="text-3xl font-bold mb-2">Mr. Play</h3>
                    <div className="text-xl font-semibold">Premium Slots</div>
                    <div className="text-sm opacity-90">Up to €100 Bonus</div>
                  </div>
                </div>
                <div className="lg:w-2/3 p-8">
                  <div className="flex items-center space-x-2 mb-4">
                    <span className="bg-green-100 text-green-800 px-3 py-1 rounded-full text-sm font-medium">Excellent for Slots</span>
                  </div>
                  <p className="text-lg text-gray-700 mb-6 leading-relaxed">
                    A global gaming powerhouse that combines sophisticated betting markets with cutting-edge casino technology,
                    featuring an exceptional slots library with premium titles from industry-leading providers like NetEnt, Microgaming,
                    and Evolution Gaming. Experience console-quality slots with stunning graphics and innovative features.
                  </p>
                  <div className="grid grid-cols-2 gap-4 mb-6">
                    <div className="flex items-center space-x-2">
                      <Crown className="h-5 w-5 text-book-brown" />
                      <span className="text-book-brown-dark">Extensive Slots Library</span>
                    </div>
                    <div className="flex items-center space-x-2">
                      <Zap className="h-5 w-5 text-book-brown" />
                      <span className="text-book-brown-dark">Premium Game Providers</span>
                    </div>
                    <div className="flex items-center space-x-2">
                      <Star className="h-5 w-5 text-book-brown" />
                      <span className="text-book-brown-dark">Mobile Optimization</span>
                    </div>
                    <div className="flex items-center space-x-2">
                      <ExternalLink className="h-5 w-5 text-book-brown" />
                      <span className="text-book-brown-dark">Fast Withdrawals</span>
                    </div>
                  </div>
                  <a
                    href="https://bwin.fr/"
                    target="_blank"
                    rel="noopener noreferrer nofollow sponsored"
                    className="inline-flex items-center space-x-2 bg-book-brown text-book-cream px-8 py-3 rounded-lg hover:bg-book-brown-light transition-colors duration-200 font-semibold"
                  >
                    <span>Play Premium Slots at Mr. Play</span>
                    <ExternalLink className="h-4 w-4" />
                  </a>
                </div>
              </div>
            </div>

            {/* NRG - Vertical Layout */}
            <div className="bg-gradient-to-b from-book-beige to-book-cream rounded-xl shadow-book p-8">
              <div className="text-center mb-8">
                <div className="inline-flex items-center justify-center w-20 h-20 bg-gradient-to-br from-blue-600 to-blue-800 rounded-full text-white mb-4">
                  <Star className="h-10 w-10" />
                </div>
                <h3 className="text-3xl font-bold text-book-brown mb-2">NRG</h3>
                <div className="text-xl font-semibold text-book-brown-dark mb-2">200% + 100 Free Spins</div>
                <span className="bg-green-100 text-green-800 px-3 py-1 rounded-full text-sm font-medium">Very Good for Slots</span>
              </div>

              <p className="text-lg text-gray-700 text-center mb-8 max-w-2xl mx-auto leading-relaxed">
                NRG delivers explosive gaming entertainment with high-energy slots and substantial welcome bonuses.
                The platform combines modern gaming technology with generous 200% bonuses and 100 free spins.
              </p>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-8">
                <div className="bg-white rounded-lg p-6 shadow-md">
                  <div className="flex items-center space-x-3 mb-3">
                    <div className="w-3 h-3 bg-book-brown rounded-full"></div>
                    <span className="font-semibold text-gray-800">Curated Slots Selection</span>
                  </div>
                  <p className="text-gray-600">Carefully chosen high-quality slot titles</p>
                </div>
                <div className="bg-white rounded-lg p-6 shadow-md">
                  <div className="flex items-center space-x-3 mb-3">
                    <div className="w-3 h-3 bg-book-brown rounded-full"></div>
                    <span className="font-semibold text-gray-800">High RTP Games</span>
                  </div>
                  <p className="text-gray-600">Excellent return-to-player percentages</p>
                </div>
                <div className="bg-white rounded-lg p-6 shadow-md">
                  <div className="flex items-center space-x-3 mb-3">
                    <div className="w-3 h-3 bg-book-brown rounded-full"></div>
                    <span className="font-semibold text-gray-800">Exclusive Bonuses</span>
                  </div>
                  <p className="text-gray-600">Slots-specific promotional offers</p>
                </div>
                <div className="bg-white rounded-lg p-6 shadow-md">
                  <div className="flex items-center space-x-3 mb-3">
                    <div className="w-3 h-3 bg-book-brown rounded-full"></div>
                    <span className="font-semibold text-gray-800">Professional Support</span>
                  </div>
                  <p className="text-gray-600">Expert assistance for slots players</p>
                </div>
              </div>

              <div className="text-center">
                <a
                  href="https://www.winamax.fr/"
                  target="_blank"
                  rel="noopener noreferrer nofollow sponsored"
                  className="inline-flex items-center space-x-2 bg-book-brown text-book-cream px-8 py-3 rounded-lg hover:bg-book-brown-light transition-colors duration-200 font-semibold"
                >
                  <span>Discover NRG Slots</span>
                  <ExternalLink className="h-4 w-4" />
                </a>
              </div>
            </div>

            {/* PUB - Card Layout */}
            <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
              <div className="bg-white rounded-xl shadow-book overflow-hidden">
                <div className="bg-gradient-to-br from-book-sepia to-book-brown-light p-6 text-center text-book-cream">
                  <Zap className="h-12 w-12 mx-auto mb-3" />
                  <h3 className="text-2xl font-bold">PUB</h3>
                  <div className="text-lg font-semibold">100% up to £100</div>
                  <span className="bg-blue-100 text-blue-800 px-2 py-1 rounded-full text-xs font-medium mt-2 inline-block">Good for Slots</span>
                </div>
                <div className="p-6">
                  <p className="text-book-brown-dark mb-6 text-center">
                    A beloved UK casino brand that brings authentic pub atmosphere to online slots gaming, featuring classic casino games
                    with modern convenience and generous welcome bonuses.
                  </p>
                  <div className="space-y-3 mb-6">
                    <div className="flex items-center space-x-2 text-sm">
                      <div className="w-2 h-2 bg-book-brown rounded-full"></div>
                      <span>Authentic UK Gaming</span>
                    </div>
                    <div className="flex items-center space-x-2 text-sm">
                      <div className="w-2 h-2 bg-book-brown rounded-full"></div>
                      <span>Community Focus</span>
                    </div>
                    <div className="flex items-center space-x-2 text-sm">
                      <div className="w-2 h-2 bg-book-brown rounded-full"></div>
                      <span>Fair Play Guarantee</span>
                    </div>
                    <div className="flex items-center space-x-2 text-sm">
                      <div className="w-2 h-2 bg-book-brown rounded-full"></div>
                      <span>Classic Casino Games</span>
                    </div>
                  </div>
                  <a
                    href="https://www.zeturf.fr/"
                    target="_blank"
                    rel="noopener noreferrer nofollow sponsored"
                    className="block text-center bg-book-brown text-book-cream py-3 rounded-lg hover:bg-book-brown-light transition-colors duration-200 font-semibold"
                  >
                    Explore PUB Slots
                  </a>
                </div>
              </div>

              <div className="bg-gradient-to-br from-book-beige to-book-cream rounded-xl p-6 flex flex-col justify-center">
                <h4 className="text-xl font-bold text-book-brown mb-4">Why Choose PUB for Slots?</h4>
                <ul className="space-y-3 text-gray-700">
                  <li className="flex items-start space-x-2">
                    <Crown className="h-5 w-5 text-book-brown mt-0.5 flex-shrink-0" />
                    <span>Authentic UK gaming experience</span>
                  </li>
                  <li className="flex items-start space-x-2">
                    <Star className="h-5 w-5 text-book-brown mt-0.5 flex-shrink-0" />
                    <span>Generous 100% welcome bonus</span>
                  </li>
                  <li className="flex items-start space-x-2">
                    <Zap className="h-5 w-5 text-book-brown mt-0.5 flex-shrink-0" />
                    <span>Community-focused atmosphere</span>
                  </li>
                </ul>
              </div>

              <div className="bg-gradient-to-br from-book-cream to-book-beige rounded-xl p-6 flex flex-col justify-center">
                <h4 className="text-xl font-bold text-book-brown mb-4">Slots Gaming Benefits</h4>
                <div className="space-y-4">
                  <div className="bg-white rounded-lg p-4 shadow-sm">
                    <div className="font-semibold text-book-brown">UK Authenticity</div>
                    <div className="text-sm text-book-brown-dark">Beloved British casino brand</div>
                  </div>
                  <div className="bg-white rounded-lg p-4 shadow-sm">
                    <div className="font-semibold text-book-brown">Community Focus</div>
                    <div className="text-sm text-book-brown-dark">Pub atmosphere and fair play</div>
                  </div>
                  <div className="bg-white rounded-lg p-4 shadow-sm">
                    <div className="font-semibold text-book-brown">Generous Bonuses</div>
                    <div className="text-sm text-book-brown-dark">100% welcome bonus up to £100</div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      <About />
      <Guides />
      <FAQ />
      <News />
      <Contact />
      <Footer />
      <AgeVerification />
      <CookieConsent />
    </div>
  );
};

export default Slots;
